import { HTMLAttributes, ReactElement } from "react";
import styled from "styled-components";
import { LuClockAlert } from "react-icons/lu";

import * as S from "./styled";

import {
  MdAdminPanelSettings,
  MdArrowCircleUp,
  MdCircle,
  MdCode,
  MdMenuBook,
  MdOutlineAdd,
  MdOutlineApartment as MdOutlineCompany,
  MdOutlineArrowCircleDown,
  MdOutlineArrowCircleUp,
  MdOutlineArrowDropDown,
  MdOutlineArrowDropUp,
  MdOutlineBlock,
  MdOutlineBuildCircle,
  MdOutlineCancel as MdOutlineCancelCircle,
  MdOutlineCheckCircle,
  MdOutlineChevronLeft,
  MdOutlineChevronRight,
  MdOutlineClose,
  MdOutlineConstruction,
  MdOutlineContentCopy,
  MdOutlineDelete,
  MdOutlineEdit,
  MdOutlineErrorOutline,
  MdOutlineExpandLess,
  MdOutlineExpandMore,
  MdOutlineFileDownload,
  MdOutlineFileUpload,
  MdOutlineFilterList,
  MdOutlineFolder,
  MdOutlineGridView,
  MdOutlineHelpOutline,
  MdOutlineImage,
  MdOutlineInfo,
  MdOutlineInsertDriveFile,
  MdOutlineKey,
  MdOutlineLogout,
  MdOutlineMenu,
  MdOutlineMoreVert,
  MdOutlinePerson,
  MdOutlinePhotoCamera,
  MdOutlineShare,
  MdOutlineLocalPrintshop,
  MdOutlineViewHeadline,
  MdOutlineVisibility,
  MdOutlineVisibilityOff,
  MdOutlineViewInAr,
  MdOutlineWarningAmber as MdOutlineWarning,
  MdDragHandle,
  MdLocationOn,
  MdOutlineRemoveCircleOutline,
  MdPendingActions,
  MdLocalFireDepartment,
  MdElectricBolt,
  MdOutlineCenterFocusWeak,
  MdOutlineZoomIn,
  MdPlayCircleOutline as MdOutlineVideo,
  MdRefresh,
  MdPause,
} from "react-icons/md";

import * as AonIcons from "./aon-icons";

// Manually construct objects of third-party icons needed so that the
// webpack build can tree-shake, only using the required icon components.
const MdIcons = {
  MdAdminPanelSettings,
  MdArrowCircleUp,
  MdCircle,
  MdCode,
  MdMenuBook,
  MdOutlineAdd,
  MdOutlineArrowCircleDown,
  MdOutlineArrowCircleUp,
  MdOutlineArrowDropDown,
  MdOutlineArrowDropUp,
  MdOutlineBlock,
  MdOutlineBuildCircle,
  MdOutlineCancelCircle,
  MdOutlineCheckCircle,
  MdOutlineChevronLeft,
  MdOutlineChevronRight,
  MdOutlineClose,
  MdOutlineCompany,
  MdOutlineConstruction,
  MdOutlineContentCopy,
  MdOutlineDelete,
  MdOutlineEdit,
  MdOutlineErrorOutline,
  MdOutlineExpandLess,
  MdOutlineExpandMore,
  MdOutlineFileDownload,
  MdOutlineFileUpload,
  MdOutlineFilterList,
  MdOutlineFolder,
  MdOutlineGridView,
  MdOutlineHelpOutline,
  MdOutlineImage,
  MdOutlineInfo,
  MdOutlineInsertDriveFile,
  MdOutlineKey,
  MdOutlineLogout,
  MdOutlineMenu,
  MdOutlineMoreVert,
  MdOutlinePerson,
  MdOutlinePhotoCamera,
  MdOutlineShare,
  MdOutlineLocalPrintshop,
  MdOutlineViewHeadline,
  MdDragHandle,
  MdOutlineVisibility,
  MdOutlineVisibilityOff,
  MdOutlineViewInAr,
  MdOutlineWarning,
  MdOutlineRemoveCircleOutline,
  MdLocationOn,
  MdPendingActions,
  MdLocalFireDepartment,
  MdElectricBolt,
  MdOutlineCenterFocusWeak,
  MdOutlineZoomIn,
  MdOutlineVideo,
  MdRefresh,
  MdPause,
};

type StripPrefix<
  TPrefix extends string,
  T extends string,
> = T extends `${TPrefix}${infer R}` ? R : T;

type StripSuffix<
  TSuffix extends string,
  T extends string,
> = T extends `${infer R}${TSuffix}` ? R : T;

type StripPrefixFromKeys<T, TPrefix extends string> = {
  [K in keyof T & string as StripPrefix<TPrefix, K>]: T[K];
};
type StripSuffixFromKeys<T, TSuffix extends string> = {
  [K in keyof T & string as StripSuffix<TSuffix, K>]: T[K];
};

type MdIconsTransform1 = StripPrefixFromKeys<typeof MdIcons, "Md">;
type MdIconsTransform2 = StripPrefixFromKeys<MdIconsTransform1, "Outline">;
type MdIconsTransformFinal = StripSuffixFromKeys<MdIconsTransform2, "Outline">;

// Remove "MdOutline" or "Outline" prefixes and/or "Outline" suffix from
// Material Design icon names
const transformIconName = (name: keyof typeof MdIcons) => {
  // TS doesn't know that our transforms here result in a valid key.
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  return name
    .replace(/^(Md)/, "")
    .replace(/^(Outline)/, "")
    .replace(/(Outline)$/, "") as keyof MdIconsTransformFinal;
};
// oxlint-disable-next-line typescript/no-unsafe-type-assertion
const MdIconsTransformedKeys = Object.fromEntries(
  Object.entries(MdIcons).map(([iconName, icon]) => [
    transformIconName(iconName),
    icon,
  ])
) as MdIconsTransformFinal;

const LucideIcons = { ClockAlert: LuClockAlert };

const AonIconVariants = Object.keys(AonIcons);
const MdIconVariants = Object.keys(MdIconsTransformedKeys);
const LucideIconVariants = Object.keys(LucideIcons);

// export icon names for Storybook story, in order to render all icons from a list
export const iconNames = [
  ...AonIconVariants,
  ...MdIconVariants,
  ...LucideIconVariants,
];

export type IconVariant =
  | (typeof AonIconVariants)[number]
  | (typeof MdIconVariants)[number]
  | (typeof LucideIconVariants)[number];

const iconRegistry = {
  ...AonIcons,
  ...MdIconsTransformedKeys,
  ...LucideIcons,
};

type IconProps = HTMLAttributes<HTMLSpanElement> & {
  variant: IconVariant;
  label?: string;
};

const _Icon = (props: IconProps): ReactElement => {
  const { variant, label, ...restProps } = props;

  // returns object with different fill style for given icon variant
  const IconComponent = iconRegistry[variant];

  if (!IconComponent) {
    throw new Error(`Icon variant "${variant}" is invalid.`);
  }

  return (
    <S.Icon tabIndex={restProps.onClick ? 0 : -1} {...restProps}>
      <IconComponent aria-label={label} style={{ fontSize: "inherit" }} />
    </S.Icon>
  );
};

export const Icon = styled(_Icon)``;
